import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { LocationSnapshotFilters } from "../types/LocationSnapshotFilters";
import { SuspectsAltHeatMap } from '../types/SuspectsAltHeatMap';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from "src/environments/environment";
import { SharedService } from "src/app/services/SharedService";
import { SuspectHeatMapService } from "../services/suspect-heatmap.service";
import { DxDataGridComponent } from "devextreme-angular";

@Component({
    selector: 'suspects-alt-heat-map',
    styleUrls: ['suspects-alt-heat-map.component.scss'],
    templateUrl: 'suspects-alt-heat-map.component.html'
})

export class SuspectsAltHeatMapComponent implements OnChanges {

    @ViewChild('SuspectAltHeatMapGrid', { static: true }) suspectAltHeatMapGrid: DxDataGridComponent;

    @Input()
    suspectAltHeatMap: SuspectsAltHeatMap;

    @Input()
    filters: LocationSnapshotFilters;

    @Output() export = new EventEmitter();

    mapConfigs: Array<{ mapName: string, displayName: string, viewportDivID: string, legendDivID: string }> = [
        {
            mapName: "MTD", displayName: '',
            viewportDivID: "suspects-alt-heatmap1-viewport", legendDivID: "suspects-alt-heatmap1-legend"
        },
        {
            mapName: "MTD_NoAP", displayName: 'Predicted Volume Without AP Accounts',
            viewportDivID: "suspects-alt-heatmap2-viewport", legendDivID: "suspects-alt-heatmap2-legend"
        },
    ];

    header1_line1: string = '';
    header2: string = '';
    header3_line1: string = '';
    header3_line2: string = '';
    all_headers: any;
    showInfoText = false;
    hideLegend1 = true;
    hideLegend2 = true;
    header4: string = '';
    header5: string = '';

    loader: Loader;
    google: any;
    altHeatMap1: google.maps.Map;
    altHeatMap2: google.maps.Map;
    infoWindow: google.maps.InfoWindow;

    constructor(private sharedSvc: SharedService, private suspectHeatMapService: SuspectHeatMapService) {

    }

    ngAfterViewInit(): void {

        //This loads the google javascript API once.
        new Loader({
            apiKey: environment.gmaps,
            version: 'weekly'
        }).load().then((google) => {
            this.google = google;
        });

    }

    ngOnChanges(changes: SimpleChanges): void {

        //Set headers if required data is received.
        if (this.filters.siteNames && this.filters.globalSegments && this.filters.radiusAroundEachPlant) {
            this.setHeaders();
        }

        let mapViewport1 = document.getElementById("suspects-alt-heatmap1-viewport");
        let mapViewport2 = document.getElementById("suspects-alt-heatmap2-viewport");

        //The application-data contains 5 main data arrays. 
        //Paint the map and populate datagrid if there is data in any of these arrays.
        if (this.suspectAltHeatMap.demandForPlantHeatMap.length > 0
            || this.suspectAltHeatMap.apPlantsData.length > 0
            || this.suspectAltHeatMap.competitorPlantsData.length > 0
            || this.suspectAltHeatMap.competitorAPPlantsData.length > 0
            || this.suspectAltHeatMap.proposedPlantsData.length > 0) {
            this.showInfoText = false;
            if (mapViewport1) mapViewport1.innerHTML = "";
            if (mapViewport2) mapViewport2.innerHTML = "";
            this.suspectHeatMapService.bounds = null;
            this.initMap();
        } else { //If main data arrays are empty, just show some info inside map viewport.
            if (mapViewport1) mapViewport1.innerHTML = "<div>No data available. Map cannot be generated.</div>";
            if (mapViewport2) mapViewport2.innerHTML = "<div>No data available. Map cannot be generated.</div>";
            this.showInfoText = true;
        }
    }

    initMap() {
        this.generateMap1();
        this.generateMap2();
    }

    async generateMap1() {

        this.altHeatMap1 = new this.google.maps.Map(document.getElementById("suspects-alt-heatmap1-viewport") as HTMLElement, {
            zoom: 6,
            mapTypeId: "terrain",
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            disableDoubleClickZoom: true,
            mapId: environment.mapId
        });

        await this.suspectHeatMapService.plotHeatMapTiles(this.altHeatMap1, this.suspectAltHeatMap.demandForPlantHeatMap, "suspects-alt-heatmap1-legend", true, this.google, true);

        this.hideLegend1 = false;

        await this.suspectHeatMapService.plotCompetitorSites(this.altHeatMap1, this.suspectAltHeatMap.competitorPlantsData, this.google);

        await this.suspectHeatMapService.plotAPPlantSites(this.altHeatMap1, this.suspectAltHeatMap.apPlantsData, this.google, this.filters);

        await this.suspectHeatMapService.plotCompetitorAPSites(this.altHeatMap1, this.suspectAltHeatMap.competitorAPPlantsData, this.google);

        await this.suspectHeatMapService.plotBullsEye(this.altHeatMap1, this.suspectAltHeatMap.bullsEyeData, this.google);

        if (this.sharedSvc.UserRoles.isIGPower) {
            await this.suspectHeatMapService.plotProposedPlantSites(this.altHeatMap1, this.suspectAltHeatMap.proposedPlantsData, this.google);
        }

        this.suspectHeatMapService.setFeatureStyles(this.altHeatMap1);

        this.suspectHeatMapService.adjustSuspectsMapViewport(this.altHeatMap1);

    }

    async generateMap2() {

        this.altHeatMap2 = new this.google.maps.Map(document.getElementById("suspects-alt-heatmap2-viewport") as HTMLElement, {
            zoom: 6,
            mapTypeId: "terrain",
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            disableDoubleClickZoom: true,
            mapId: environment.mapId
        });

        await this.suspectHeatMapService.plotHeatMapTiles(this.altHeatMap2, this.suspectAltHeatMap.demandForPlantHeatMap, "suspects-alt-heatmap2-legend", false, this.google, false);

        this.hideLegend2 = false;

        await this.suspectHeatMapService.plotCompetitorSites(this.altHeatMap2, this.suspectAltHeatMap.competitorPlantsData, this.google);

        await this.suspectHeatMapService.plotAPPlantSites(this.altHeatMap2, this.suspectAltHeatMap.apPlantsData, this.google, this.filters);

        await this.suspectHeatMapService.plotCompetitorAPSites(this.altHeatMap2, this.suspectAltHeatMap.competitorPlantsData, this.google);

        await this.suspectHeatMapService.plotBullsEye(this.altHeatMap2, this.suspectAltHeatMap.bullsEyeData, this.google);

        if (this.sharedSvc.UserRoles.isIGPower) {
            await this.suspectHeatMapService.plotProposedPlantSites(this.altHeatMap2, this.suspectAltHeatMap.proposedPlantsData, this.google);
        }

        this.suspectHeatMapService.setFeatureStyles(this.altHeatMap2);

        this.suspectHeatMapService.adjustSuspectsMapViewport(this.altHeatMap2);

    }

    setHeaders() {
        this.header1_line1 = `Volume for Segments: ${this.filters.globalSegments}`;

        this.header2 = "AIR PRODUCTS CONFIDENTIAL - INTERNAL USE ONLY";

        this.header3_line1 = 'Isoline/circles are only used for visual guidance and not used for or representative of any calculations in the reports.';
        this.header3_line2 = 'Green squares are AP plants.  Black dots are competitor plants.  Green diamonds are AP proposed plants. You may mouse-over any of these to get details.';
        this.header4 = 'Predicted Volume Without AP Accounts';
        this.header5 = 'Predicted Volume With AP Accounts'
        this.all_headers = {
            header1_line1: this.header1_line1,
            header2: this.header2,
            header3_line1: this.header3_line1,
            header3_line2: this.header3_line2,
            header4: this.header4,
            header5: this.header5
        };

    }

    exportExcel($event) {
        this.export.emit();
    }
}

